import { api } from '@/lib/api-client'

// ── Backend contract (per MOBILE_API_SPEC §6 + Slice C.1/C.2) ───────────────
// 4 endpoints power the notification feed across web + mobile:
//   GET  /notifications              paginated feed (also returns unread_count)
//   GET  /notifications/unread-count lightweight badge endpoint
//   POST /notifications/{id}/read    mark single (idempotent)
//   POST /notifications/read-all     bulk mark
//
// 17 event types fire today (Slice C.1/C.2 + account.password_reset from
// BE-21, minus the retired site-rep events). Each row carries a `metadata`
// blob whose shape varies by type; we discriminate on `type` when rendering
// body + deep-link.

// ── Event type union (locked with backend) ─────────────────────────────────

export type NotificationType =
  // Issue lifecycle (Slice C.1)
  | 'issue.raised'
  | 'issue.resolved_by_hod'
  | 'issue.rejected_by_hod'
  | 'issue.completed'
  // Change-request workflow (Slice C.1)
  | 'change_request.submitted'
  | 'change_request.approved'
  | 'change_request.rejected'
  // Issue lifecycle (Slice C.2)
  | 'issue.remark_added'
  | 'issue.closed_by_admin'
  // Assignment lifecycle (Slice C.2)
  | 'department.assigned'
  | 'department.unassigned'
  | 'task.assigned'
  | 'task.missed'
  // Account state
  | 'account.reactivated'
  | 'account.password_reset'
  | 'account.phone_changed_by_hod'

// ── Notification row shape ─────────────────────────────────────────────────
// `metadata` is per-type. Render the row from title/body provided by backend
// (no follow-up fetch needed). FK fields (issue_id / assignment_id /
// change_request_id) drive deep-linking when present.

export interface Notification {
  id: string
  type: NotificationType
  title: string
  body: string
  issue_id: string | null
  assignment_id: string | null
  change_request_id: string | null
  metadata: Record<string, unknown>
  read_at: string | null
  created_at: string
}

// ── List feed ──────────────────────────────────────────────────────────────

export interface ListNotificationsParams {
  unread_only?: boolean
  page?: number
  page_size?: number
}

export interface NotificationsPagination {
  page: number
  page_size: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_previous: boolean
}

export interface NotificationsResponse {
  items: Notification[]
  pagination: NotificationsPagination
  unread_count: number
}

export function listNotifications(
  params: ListNotificationsParams = {},
): Promise<NotificationsResponse> {
  return api
    .get<NotificationsResponse | { items: Notification[]; unread_count?: number }>(
      '/notifications',
      { params },
    )
    .then((d) => {
      // Defensive: backend may return either an envelope or a raw items list.
      if ('pagination' in d) {return d}
      return {
        items: d.items ?? [],
        unread_count: d.unread_count ?? 0,
        pagination: {
          page: params.page ?? 1,
          page_size: params.page_size ?? (d.items?.length ?? 0),
          total_count: d.items?.length ?? 0,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      }
    })
}

// ── Unread count (lightweight badge endpoint) ──────────────────────────────

export interface UnreadCountResponse {
  unread_count: number
}

export function getUnreadCount(): Promise<UnreadCountResponse> {
  return api.get<UnreadCountResponse>('/notifications/unread-count')
}

// ── Mark read (single + all) ───────────────────────────────────────────────

export interface MarkReadResponse {
  id: string
  read_at: string
}

export function markNotificationRead(id: string): Promise<MarkReadResponse> {
  return api.post<MarkReadResponse>(`/notifications/${id}/read`, undefined, {
    showToast: false,
  })
}

export interface MarkAllReadResponse {
  marked_count: number
  unread_count: 0
}

export function markAllNotificationsRead(): Promise<MarkAllReadResponse> {
  return api.post<MarkAllReadResponse>('/notifications/read-all', undefined, {
    showToast: false,
  })
}
